import cn from 'classnames';
import { useRouter } from 'next/dist/client/router';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useEffect, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import InputMask from 'react-input-mask';
import useSWR from 'swr';

import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';

import SeatsPreview from '~components/Purchase/SeatsPreview';
import UserLoyalty from '~components/Purchase/UserLoyalty';
import OverflowContainer from '~components/common/OverflowContainer';
import CardThumbnail from '~components/ui/CardThumbnail';
import Checkbox from '~components/ui/Checkbox';
import Form from '~components/ui/Form';
import Input, { InputLocked } from '~components/ui/Input';
import inputStyles from '~components/ui/Input/Input.module.css';
import Notification from '~components/ui/Notification';
import { getFromAPI, postToAPI } from '~utils/api/common';
import useAuthUser from '~utils/hooks/useAuthUser';
import useGlobalState from '~utils/hooks/useGlobalState';
import useLocalStorage from '~utils/hooks/useLocalStorage';
import { formatPrice } from '~utils/hooks/usePrice';
import usePurchase from '~utils/hooks/usePurchase';
import { normalizeTickets } from '~utils/normalizers';

import {
  Buy,
  BuyResponse,
  ErrorResponse,
  PaymentMethod,
  Purchase,
  SessionSeatsResponse,
  Show,
  Ticket,
} from '~types';

const DEFAULT_VALUES = {
  cardNumber: '',
  name: '',
  lastname: '',
  cardExp: '',
  cardCVV: '',
  email: '',
  phone: '',
  cardName: undefined,
  savePaymentMethod: false,
};

type FormTypes = {
  cardNumber: string;
  name: string;
  lastname: string;
  cardExp: string;
  cardCVV: string;
  email: string;
  phone: string;
  savePaymentMethod: boolean;
  cardName: string | undefined;
};

interface Props {
  showTickets: Ticket[];
  show: Show;
  visible: boolean;
}

const Tickets = dynamic(() => import('../Tickets/Tickets'), {
  ssr: false,
  loading: () => <span>Cargando boletos...</span>,
}) as typeof import('../Tickets/Tickets').default;

const Payment: FC<Props> = ({ showTickets, visible, show }) => {
  const [, setOverlayProps] = useGlobalState('OverlayMessage');
  const [localBuys, setLocalBuys] = useLocalStorage<Buy[]>('Buys', []);
  const { query, replace, asPath } = useRouter();
  const user = useAuthUser();
  const { tickets, seats, transIdTemp, updatePurchase, resetPurchase, total, bookingFee } =
    usePurchase();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
  const form = Form.useForm<FormTypes>({ defaultValues: DEFAULT_VALUES });
  const { data: cards } = useSWR<PaymentMethod[]>(!!user ? '/payment-methods' : null, {
    revalidateOnFocus: false,
  });

  const { data: roomLayout } = useSWR<SessionSeatsResponse>(
    `/shows/seats/${query.session}`,
    (url: string) => getFromAPI(url, { notifyErrors: false }),
    {
      revalidateOnMount: false,
    },
  );

  useEffect(() => {
    if (user) {
      let formValues = DEFAULT_VALUES;

      const { email, phone, name, lastname } = user;
      formValues = {
        ...DEFAULT_VALUES,
        name,
        lastname,
        email: email ?? '',
        phone: phone ?? '',
      };

      if (paymentMethod) {
        const { expMonth, expYear, phone } = paymentMethod;
        formValues = {
          ...formValues,
          cardExp: `${expMonth}/${expYear}`.replaceAll('_', ''),
          phone,
        };
        form.setFocus('cardCVV');
      }

      form.reset(formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, paymentMethod]);

  const saveCardField = useRef(false);
  saveCardField.current = form.watch('savePaymentMethod', false);

  const onSubmit: SubmitHandler<FormTypes> = (values) => {
    const data = {
      ...values,
      PaymentMethodId: paymentMethod?.id || null,
      seats,
      tickets: normalizeTickets(tickets),
      sessionId: query.session,
      transIdTemp,
      bookingFee,
    };
    setOverlayProps({
      title: 'Procesando la compra',
      message: 'Espera un momento por favor.',
      icon: <LoadingOutlined className="text-5xl" />,
      visible: true,
    });
    updatePurchase({ processingPurchase: true });
    postToAPI<BuyResponse>('/buys', data, { notifyErrors: false })
      .then((response) => {
        if (response.confirmationNumber) {
          setOverlayProps({
            title: 'Compra exitosa.',
            message: 'Generando tu pase de entrada.',
            icon: <CheckCircleFilled className="text-3xl" />,
            type: 'success',
            visible: true,
          });
          setLocalBuys([...localBuys, response.buy]);
          replace(`/compra/${response.confirmationNumber}?gracias`).then(() => {
            resetPurchase();
          });
        }
      })
      .catch((e: ErrorResponse) => {
        console.log(e);
        const message = 'Ocurrió un error en el servidor al procesar tu compra';

        setOverlayProps({
          title: 'Error',
          message: e.statusCode === 500 ? message : e.message,
          icon: <CloseCircleFilled className="text-5xl" />,
          type: 'error',
          visible: true,
          children:
            e?.data?.type === 'SEAT_SELECTION_FAIL' ? (
              <Link
                href={asPath.replace('?step=pago', '?step=inicio')}
                shallow
                className="btn text-neutral-800"
                onClick={() => setOverlayProps({})}
              >
                Seleccionar otros asientos
              </Link>
            ) : (
              <button className="btn text-black" onClick={() => setOverlayProps({})}>
                Aceptar
              </button>
            ),
        });
        const newData: Partial<Purchase> = { processingPurchase: false };
        if (e?.data?.transIdTemp) {
          newData.transIdTemp = e?.data?.transIdTemp;
        }
        if (e?.data?.buy?.seats) {
          newData.reservedSeats = e?.data?.buy?.seats;
        }
        if (e?.data?.expDate) {
          newData.expDate = e?.data?.expDate;
        }
        updatePurchase(newData);
        console.log(e);
      });
  };

  useEffect(() => {
    if (query.step === 'pago' && seats?.length === 0) {
      replace(asPath.replace('?step=pago', '?step=inicio'), undefined, { shallow: true }).then(
        () => {
          Notification.info({ title: 'El primer paso es seleccionar tus asientos.' });
        },
      );
    }
  }, [query]);

  return (
    <section className={cn('container', { hidden: !visible })}>
      <div className="my-10">
        <div className="flex flex-row items-center space-x-6">
          <Link
            href={asPath.replace('?step=pago', '?step=inicio')}
            shallow
            className="btn translate-y-[-4px] dark:btn-dark"
          >
            <ArrowLeftOutlined />
          </Link>
          <h1 className="h2 sm:h1 mb-0">Detalles del pago</h1>
        </div>
        <p>
          Llena los datos de tu tarjeta de crédito o débito, compra tus entradas ahora y no hagas
          filas.
        </p>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className=" grid grid-cols-12 gap-y-10 md:gap-x-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="border border-dashed border-line lg:mr-12">
              <div className="bg-neutral-50 py-6 px-4 dark:bg-neutral-800 md:py-8 md:px-6">
                <UserLoyalty total={total} />
              </div>
              {cards && cards?.length > 0 && (
                <div className="border-b border-dashed border-line py-8 px-4 md:p-8">
                  <h3 className="h5 mb-4">Tus tarjetas guardadas:</h3>
                  <OverflowContainer itemsWithoutPadding>
                    {cards.map((item) => (
                      <div key={item.id} className="px-2">
                        <CardThumbnail
                          data={item}
                          onSelect={setPaymentMethod}
                          onDeselect={() => {
                            setPaymentMethod(undefined);
                          }}
                          selected={paymentMethod?.id === item.id}
                        />
                      </div>
                    ))}
                  </OverflowContainer>
                </div>
              )}
              <div className="py-8 px-4 md:p-8">
                <div className="grid grid-cols-12 gap-x-2 sm:gap-x-6 gap-y-6">
                  {!paymentMethod && (
                    <>
                      <div className="col-span-8 md:col-span-9">
                        <label htmlFor="cardNumber">Número de la tarjeta:</label>
                        <InputMask
                          autoFocus
                          mask="9999 9999 9999 9999"
                          maskPlaceholder=""
                          className={inputStyles.control}
                          {...form.register('cardNumber', { required: true })}
                        />
                      </div>
                      <div className="col-span-4 self-center md:col-span-3">
                        <div className="mt-6 flex flex-row gap-1">
                          <Image
                            src="/mock/visa.png"
                            width={40}
                            height={24}
                            className="w-full max-w-[40px]"
                            alt="Imagen VISA"
                          />
                          <Image
                            src="/mock/mastercard.png"
                            width={40}
                            height={24}
                            className="w-full max-w-[40px]"
                            alt="Imagen Mastercard"
                          />
                          <Image
                            src="/mock/amex.png"
                            width={40}
                            height={24}
                            className="w-full max-w-[40px]"
                            alt="Imagen AMEX"
                          />
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label htmlFor="name">Nombre:</label>
                        <Input {...form.register('name', { required: true })} />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label htmlFor="lastname">Apellido:</label>
                        <Input {...form.register('lastname', { required: true })} />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label htmlFor="cardExp">Fecha de expiración:</label>
                        <InputMask
                          mask="99/99"
                          placeholder="MM/AA"
                          className={inputStyles.control}
                          {...form.register('cardExp', { required: true })}
                        />
                      </div>
                    </>
                  )}
                  <div className="col-span-12 md:col-span-6">
                    <label htmlFor="cardCVV">Código de seguridad:</label>
                    <Input
                      autoFocus
                      type="password"
                      maxLength={4}
                      {...form.register('cardCVV', { required: true })}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <label htmlFor="email">Correo electrónico:</label>
                    {user ? (
                      <InputLocked type="email" {...form.register('email', { required: true })} />
                    ) : (
                      <Input type="email" {...form.register('email', { required: true })} />
                    )}
                  </div>
                  {!paymentMethod && (
                    <>
                      <div className="col-span-12 md:col-span-6">
                        <label htmlFor="phone">Teléfono:</label>
                        <InputMask
                          mask="+52 999 999 9999"
                          placeholder="+52"
                          maskPlaceholder=""
                          className={inputStyles.control}
                          {...form.register('phone', { required: true })}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-6 md:mt-6 md:self-center">
                        {!!user ? (
                          <Checkbox
                            id="guardaMetodoPago"
                            label="Guardar tarjeta"
                            disabled={paymentMethod !== undefined}
                            {...form.register('savePaymentMethod')}
                          />
                        ) : (
                          <span className="text-sm text-support">
                            Inicia sesión para que puedas almacenar esta tarjeta en tu cuenta
                            CitiPremia y la uses en tus próximas compras.
                          </span>
                        )}
                      </div>
                      {saveCardField.current === true && (
                        <div className="col-span-12 md:col-span-6">
                          <label htmlFor="cardName">Agrega un nombre a tu tarjeta:</label>
                          <Input {...form.register('cardName', { required: true })} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 col-span-12">
            <h2 className="h2">Asientos seleccionados</h2>
            {roomLayout && (
              <div className="hidden lg:block">
                <SeatsPreview data={roomLayout} roomType={show.roomType} />
                <div className="my-4 text-center">
                  <Link
                    href={asPath.replace('?step=pago', '?step=inicio')}
                    shallow
                    className="btn btn-sm dark:btn-dark"
                  >
                    <ArrowLeftOutlined />
                    <span>Cambiar asientos</span>
                  </Link>
                </div>
              </div>
            )}
            <p>Aquí puedes cambiar el tipo de boleto para cada asiento</p>
            {Tickets && <Tickets ticketsPrices={showTickets} locked={true} />}
          </div>
          <div className="col-span-12 space-y-4 md:order-2 md:col-span-4">
            <button className="btn btn-primary btn-lg w-full" type="submit">
              Completar compra
            </button>
            <small className="block text-center">
              Al comprar acepto los{' '}
              <a href="/docs/terminos-y-condiciones.pdf" className="underline" target="_blank">
                Términos y condiciones
              </a>{' '}
              y el{' '}
              <a href="/aviso-privacidad" className="underline" target="_blank">
                Aviso de privacidad
              </a>{' '}
              de Citicinemas
            </small>
          </div>
          <ul className="col-span-12 list-disc space-y-2 md:order-1 md:col-span-8 mx-4">
            <li>
              <small>
                Los Datos Personales y Financieros que le son solicitados,serán tratados con las
                finalidades primarias de comprar y confirmar los boletos que adquiere.
              </small>
            </li>
            <li>
              <small>
                Los boletos son válidos únicamente para el día y horario seleccionado. No se
                realizarán reembolsos ni cambios de horarios una vez realizada la compra.
              </small>
            </li>
            {!!bookingFee ? (
              <li>
                <small>
                  Se realizará un cargo por servicio por cada boleto ({formatPrice(bookingFee || 0)}{' '}
                  MXN) en la compra
                </small>
              </li>
            ) : null}
          </ul>
        </div>
      </form>
    </section>
  );
};

export default Payment;
